import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useTRPC } from "@/trpc";
import { MONITORING_COPY } from "../monitoring-constants";
import { useInvalidateMonitoring } from "../use-monitoring-invalidation";

type Props = {
  organizationBusinessUnitId: string;
  providerPoolId: string;
  poolName: string;
  /** Every non-archived rule that targets the pool. */
  programIds: string[];
  onClose: () => void;
};

export function TurnOffPoolDialog({
  organizationBusinessUnitId,
  providerPoolId,
  poolName,
  programIds,
  onClose,
}: Props) {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitoring();
  const disable = useMutation(
    trpc.psv.monitoring.disableMonitoring.mutationOptions(),
  );

  async function turnOff() {
    try {
      for (const programId of programIds) {
        await disable.mutateAsync({
          organizationBusinessUnitId,
          programId,
          target: { providerPoolId },
        });
      }
    } catch {
      // The failure is shown in the dialog from `disable.error`.
      await invalidate();
      return;
    }
    await invalidate();
    toast.success("Monitoring turned off.");
    onClose();
  }

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Turn off monitoring for {poolName}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {MONITORING_COPY.poolMembersStop} Past results are kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {disable.isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {disable.error.message}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button disabled={disable.isPending} onClick={() => void turnOff()}>
            Turn off
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
